import showdown from 'showdown'
import {logger, Time, UIUtil, Util} from 'yougile-util'
import {Hub, StickerColor, Tag} from 'yougile-model'
import {youGileUserId} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Backend} from '@common/backend'
import {Board} from '@extensions/board'
import {Project} from '@extensions/project'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {BoardOption} from '../import-from-trello-store'
import {
  TrelloAction,
  TrelloBoard,
  TrelloCard,
  TrelloChecklist,
  TrelloLabel
} from './trello-board'
import {Attachments, TrelloList} from './trello-board'
import {fetchBoard} from './trello-fetch'

export const TASKS_BATCH_SIZE = 500
export const TASKS_BATCH_DELAY = 5000

export interface TrelloBoardData {
  boardData: TrelloBoard
  board: Hub
  boardActions: TrelloAction[]
  attachments: Attachments[]
  covers: Attachments[]
}

const converter = new showdown.Converter()

const getDescriptionImage = (url: string, previewUrl: string): string => {
  return `
  <div>
    <a target="_blank" href="${url}">
      <img class="file-link-prev" src="${previewUrl}">
    </a>
  </div>
  `
}

const getDescriptionFile = (url: string): string => {
  return `
  <div>
    <a target="_blank" href="${url}">${url.split('/').pop()}</a>
  </div>
  `
}

interface TaskIndex {
  attachments: {[cardId: string]: Attachments[]}
  covers: {[cardId: string]: Attachments[]}
  checklists: {[cardId: string]: TrelloChecklist[]}
  actions: {[cardId: string]: TrelloAction[]}
  labels: {[labelId: string]: TrelloLabel}
  stickerMap: {[labelId: string]: Tag}
}

const createTask = async (
  card: TrelloCard,
  index: TaskIndex,
  column: Hub
): Promise<void> => {
  const task = Task.createTask(card.name, {
    location: column.id,
    noAutoSubscribe: true,
    deadline: card.badges.due
      ? {
          deadline: Time.toTimestamp(new Date(card.badges.due)),
          startDate: card.badges.start
            ? Time.toTimestamp(new Date(card.badges.start))
            : undefined,
          timestamp: Time.toTimestamp(new Date(card.dateLastActivity)),
          by: youGileUserId,
          withTime: true,
          notifyBefore: card.dueReminder * Time.minute
        }
      : undefined
  })
  if (!task) {
    logger.warn('Task import from trello failed', card.name)
    return
  }

  card.dueComplete &&
    Task.insertInGTD(task, 'done', 'create-trello-project -> createTask')

  if (card.closed) {
    await Task.changeArchived(task, true)
  }

  if (card.desc.length) {
    const description = converter.makeHtml(card.desc)
    task.description = description
  }

  ;(index.attachments[card.id] ?? []).forEach(attachment => {
    const url = Backend.toDataUrl(attachment.url)
    const previewUrl = Backend.toDataUrl(attachment.previewUrl)
    if (url === undefined || previewUrl === undefined) {
      return
    }
    task.description += UIUtil.isImageName(url)
      ? getDescriptionImage(url, previewUrl)
      : getDescriptionFile(url)
  })
  ;(index.checklists[card.id] ?? []).forEach(checklist => {
    const checklistId = Task.addChecklist(task, checklist.name, true)
    checklist.checkItems.forEach(checkItem => {
      Task.addSubtask(
        task,
        checklistId,
        checkItem.name,
        checkItem.state === 'complete'
      )
    })
  })
  ;(index.actions[card.id] ?? []).forEach(action => {
    if (action.type === 'createCard') {
      task.timestamp = Time.toTimestamp(new Date(action.date))
    } else if (
      action.type === 'commentCard' &&
      action.data.text &&
      passMessageText(action.data.text)
    ) {
      task.chat.addMessage(
        youGileUserId,
        {text: action.data.text},
        {
          timestamp: Time.toTimestamp(new Date(action.date)),
          properties: {
            displayName: action.memberCreator?.fullName ?? 'YouGile'
          }
        }
      )
    }
  })

  if (card.cover.idAttachment || card.cover.sharedSourceUrl) {
    const attachment = index.covers[card.id]?.find(
      a => a.attachmentId === (card.cover.idAttachment ?? 'unsplash')
    )
    task.data.image = attachment?.url
  }
  if (card.idLabels?.length) {
    ;[...card.idLabels].reverse().forEach(id => {
      const sticker = index.stickerMap[id]
      if (sticker) {
        Sticker.pin(sticker, task.id, task, sticker.states.order[0])
      }
    })
  }

  task.commit()
}

const createTasks = async (
  cards: TrelloCard[],
  index: TaskIndex,
  column: Hub
): Promise<void> => {
  if (!cards.length) {
    return
  }
  const firstCards = cards.splice(0, TASKS_BATCH_SIZE)
  await SyncCommitHandler.asyncDoInBatch(async () => {
    let counter = 0
    for (const card of firstCards) {
      counter++
      if (counter % 100 === 0) {
        await Util.wait(10)
      }
      await createTask(card, index, column)
    }
  })
  await Util.wait(TASKS_BATCH_DELAY)
  await createTasks(cards, index, column)
}

const createTrelloColumn = async (
  {board, boardData}: TrelloBoardData,
  list: TrelloList,
  index: TaskIndex
): Promise<void> => {
  const column = Board.addColumn(list.name, board)
  const trelloCardsToProcess = boardData.cards
    .filter(card => card.idList === list.id)
    .reverse()
  await createTasks(trelloCardsToProcess, index, column)
}

const passMessageText = (text: string): boolean => {
  const reg = new RegExp('\\[.*?\\..*?\\]\\(https:\\/\\/trello\\.com\\/.*?\\)')
  return !reg.test(text)
}

const createTrelloLabels = (
  {board}: TrelloBoardData,
  index: TaskIndex
): {[labelId: string]: Tag} => {
  const labelMap: {[labelId: string]: Tag} = {}
  const box = Sync.get(board)
  Object.entries(index.labels).forEach(([labelId, label]) => {
    const toCamel = (str: string): string =>
      str.replace(/[-_]([a-z])/g, g => g[1].toUpperCase())
    const title = toCamel(label.name ? label.name : label.color ?? 'empty')
    const sticker = Sticker.create(box, title, 'string')
    if (sticker) {
      const ctl = stickerCtl.String
      const space = '‏‏‎ ‎'
      const name = label.name ? toCamel(label.name) : space.repeat(10)
      const color: StickerColor =
        labelsColorMap[label.color ?? 'black'] ?? 'sticker-gray'
      const state = {id: ctl.makeShortId(), name, color}
      ctl.addState(sticker, state)

      Sticker.addToBoard(board, sticker)
      labelMap[labelId] = sticker
    }
  })
  return labelMap
}

const createTrelloBoard = async (
  data: TrelloBoardData,
  index: TaskIndex
): Promise<void> => {
  Board.removeColumn(Board.listColumns(data.board)[0])
  for (const list of data.boardData.lists) {
    await createTrelloColumn(data, list, index)
  }
}

const makeIndex = (data: TrelloBoardData): TaskIndex => {
  const result: TaskIndex = {
    attachments: {},
    covers: {},
    checklists: {},
    actions: {},
    labels: {},
    stickerMap: {}
  }
  for (const attachment of data.attachments) {
    result.attachments[attachment.cardId] ??= []
    result.attachments[attachment.cardId].push(attachment)
  }
  for (const cover of data.covers) {
    result.covers[cover.cardId] ??= []
    result.covers[cover.cardId].push(cover)
  }
  for (const checklist of data.boardData.checklists) {
    result.checklists[checklist.idCard] ??= []
    result.checklists[checklist.idCard].push(checklist)
  }
  for (const action of data.boardActions) {
    const id = action?.data?.card?.id
    if (id) {
      result.actions[id] ??= []
      result.actions[id].push(action)
    }
  }
  for (const card of data.boardData.cards) {
    for (const label of card.labels) {
      result.labels[label.id] = label
    }
  }

  return result
}

export const createTrelloProject = async (
  selectedBoards: BoardOption[],
  setLoadingProgress: (perc: number) => void
): Promise<Hub> => {
  const box = Sync.getCurrentSandbox()
  const project = Project.createNew(box.companyId, 'Trello')
  Project.removeBoard(Project.listBoards(project)[0])

  let i = 0
  for (const boardOpt of selectedBoards) {
    const currentProgress = (i + 0.05) / selectedBoards.length
    setLoadingProgress(currentProgress)
    const board = Project.addBoard(boardOpt.label, project)
    const data = await fetchBoard(
      boardOpt.value,
      board,
      setLoadingProgress,
      currentProgress,
      0.9 / selectedBoards.length
    )
    setLoadingProgress((i + 0.95) / selectedBoards.length)
    if (data) {
      const index = makeIndex(data)
      index.stickerMap = createTrelloLabels(data, index)
      await createTrelloBoard(data, index)
    }
    i++
  }
  setLoadingProgress(1)
  return project
}

const labelsColorMap: {[key: string]: StickerColor} = {
  green_light: 'sticker-green',
  green: 'sticker-green',
  green_dark: 'sticker-green',
  yellow_light: 'sticker-orange',
  yellow: 'sticker-orange',
  yellow_dark: 'sticker-orange',
  orange_light: 'sticker-orange',
  orange: 'sticker-orange',
  orange_dark: 'sticker-orange',
  red_light: 'sticker-red',
  red: 'sticker-red',
  red_dark: 'sticker-red',
  purple_light: 'sticker-violet',
  purple: 'sticker-violet',
  purple_dark: 'sticker-violet',
  blue_light: 'sticker-blue',
  blue: 'sticker-blue',
  blue_dark: 'sticker-blue',
  sky_light: 'sticker-turquoise',
  sky: 'sticker-turquoise',
  sky_dark: 'sticker-turquoise',
  lime_light: 'sticker-green',
  lime: 'sticker-green',
  lime_dark: 'sticker-green',
  pink_light: 'sticker-pink',
  pink: 'sticker-pink',
  pink_dark: 'sticker-pink',
  black_light: 'sticker-gray',
  black: 'sticker-gray',
  black_dark: 'sticker-gray'
}
